import React from "react";
import { Box, Button, Card, IconButton, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import { Close, Favorite, Star } from "@mui/icons-material";
import { useSession } from "../context/SessionContext";
import useGoogleReview from "../hooks/useGoogleReview";

// The invite to review the store on Google: a warm thank-you for the meal
// first, the ask second, and no pressure either way — "maybe later" is a full
// answer, and a guest who has already left a review is never asked again
// (see useGoogleReview).
//
// Shown on the Live Order screen once the meal is over — the bill has been
// printed, or the till has closed the order — and only by a store that pasted
// its own review link into denzo-admin > Store > Settings.

const GOLD = "#F79009";

const twinkle = keyframes`
  0% { transform: scale(0.6) rotate(-18deg); opacity: 0; }
  60% { transform: scale(1.18) rotate(4deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const beat = keyframes`
  0%, 100% { transform: scale(1); }
  28% { transform: scale(1.16); }
  55% { transform: scale(0.98); }
`;

// Google's own mark, so the button plainly says where it goes.
function GoogleMark({ size = 18 }) {
  return (
    <Box component="svg" viewBox="0 0 48 48" sx={{ width: size, height: size, flexShrink: 0 }} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </Box>
  );
}

// Five stars, each one landing just after the one before it.
function Stars({ size = 20 }) {
  return (
    <Box sx={{ display: "flex", color: GOLD }} role="img" aria-label="Five stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          sx={{
            fontSize: size,
            animation: `${twinkle} .5s cubic-bezier(.2,.7,.3,1) ${120 + i * 90}ms both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      ))}
    </Box>
  );
}

export default function GoogleReviewCard({ orderId, sx }) {
  const { store } = useSession();
  const { show, thanked, rate, later } = useGoogleReview({ orderId });

  if (!show && !thanked) return null;

  const card = {
    p: 1.75,
    mb: 2,
    borderRadius: 2,
    borderColor: "#FEDF89",
    background: "linear-gradient(135deg, #FFFAEB, #fff 72%)",
    ...sx,
  };

  // They have just been sent to Google: the card stays one more moment to say
  // thank you, instead of blinking out from under their finger.
  if (thanked) {
    return (
      <Card variant="outlined" sx={card}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              bgcolor: GOLD,
              animation: `${beat} 1.4s ease-in-out .1s infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Favorite fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Thank you so much!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your review means the world to the team at {store?.name || "our restaurant"}. See you again soon!
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={card}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stars />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5 }}>
            Hope you enjoyed your meal!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If we got it right, a quick review on Google would make our day — and it helps other people find{" "}
            {store?.name || "us"}.
          </Typography>
        </Box>
        <IconButton size="small" onClick={later} aria-label="Maybe later" sx={{ mt: -0.5, mr: -0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
        <Button
          variant="outlined"
          onClick={rate}
          startIcon={<GoogleMark size={17} />}
          sx={{ flex: 1, py: 1, bgcolor: "#fff", borderColor: "rgba(16,24,40,0.2)", color: "text.primary" }}
        >
          Rate us on Google
        </Button>
        <Button onClick={later} color="inherit" sx={{ color: "text.secondary", flexShrink: 0 }}>
          Maybe later
        </Button>
      </Box>
    </Card>
  );
}
